import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport'
import { getSupportContact } from '../../support/supportApi'
import { PublicPageLayout, type PublicDestination } from './PublicPageLayout'

function SupportCard({ title, body, children }: { title: string; body: string; children: ReactNode }) {
  return (
    <article style={{ background: 'var(--white)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', padding: 'var(--sp-5)' }}>
      <h2 style={{ color: 'var(--brand-900)', fontSize: 'var(--fs-20)', margin: 0 }}>{title}</h2>
      <p style={{ color: 'var(--fg-muted)', fontSize: 'var(--fs-14)', lineHeight: 'var(--lh-normal)', margin: 0 }}>{body}</p>
      {children}
    </article>
  )
}

export function SupportPage({ onNavigate }: { onNavigate: (destination: PublicDestination) => void }) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [whatsappNumber, setWhatsappNumber] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    getSupportContact()
      .then(contact => { if (active) setWhatsappNumber(contact.whatsappNumber) })
      .catch(() => { if (active) setWhatsappNumber(null) })
    return () => { active = false }
  }, [])

  const whatsappHref = whatsappNumber ? `https://wa.me/${whatsappNumber.replace(/\D/g, '')}` : null
  const actionStyle = { color: 'var(--brand-500)', fontSize: 'var(--fs-14)', fontWeight: 'var(--fw-bold)', textDecoration: 'none' } as const

  return (
    <PublicPageLayout active="support" onNavigate={onNavigate}>
      <header style={{ borderBottom: '1px solid var(--border-subtle)', padding: isMobile ? 'var(--sp-8) var(--sp-6)' : 'var(--sp-12) min(8.33vw, 120px) var(--sp-10)' }}>
        <h1 style={{ color: 'var(--brand-900)', fontSize: isMobile ? 'var(--fs-30)' : 'var(--fs-48)', fontWeight: 'var(--fw-extra)', lineHeight: 'var(--lh-snug)', margin: 0 }}>{t('public.site.support.title')}</h1>
        <p style={{ color: 'var(--fg-muted)', fontSize: isMobile ? 'var(--fs-16)' : 'var(--fs-18)', lineHeight: 'var(--lh-normal)', margin: 'var(--sp-2) 0 0' }}>{t('public.site.support.subtitle')}</p>
      </header>
      <section style={{ background: 'var(--ink-50)', padding: isMobile ? 'var(--sp-8) var(--sp-6) var(--sp-16)' : 'var(--sp-12) min(8.33vw, 120px) var(--sp-20)' }}>
        <div style={{ display: 'grid', gap: 'var(--sp-4)', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(3, minmax(0, 1fr))', margin: '0 auto', maxWidth: 1200 }}>
          <SupportCard title={t('public.site.support.email.title')} body={t('public.site.support.email.body')}>
            <a href="mailto:support@tlaaq.com" style={actionStyle}>{t('public.site.support.email.address')}</a>
          </SupportCard>
          <SupportCard title={t('public.site.support.whatsapp.title')} body={t('public.site.support.whatsapp.body')}>
            {whatsappHref ? <a href={whatsappHref} target="_blank" rel="noreferrer" style={actionStyle}>{t('public.site.support.whatsapp.action')}</a> : <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--fs-14)' }}>{t('public.site.support.whatsapp.unavailable')}</span>}
          </SupportCard>
          <SupportCard title={t('public.site.support.privacy.title')} body={t('public.site.support.privacy.body')}>
            <a href="/privacy" onClick={(event) => { event.preventDefault(); onNavigate('privacy') }} style={actionStyle}>{t('public.site.support.privacy.action')}</a>
          </SupportCard>
        </div>
      </section>
    </PublicPageLayout>
  )
}
